/*
 * product-gallery-video.js
 * Controls product gallery videos: plays a video when its thumbnail is clicked,
 * keeps native controls so shoppers can pause / resume, and pauses videos
 * when their slide is left or scrolled out of view.
 *
 * Markup contract:
 *   [data-gallery-video-root]                 main gallery (Swiper element)
 *   [data-gallery-video="<media_id>"]         <video> inside a main slide
 *   [data-gallery-video-thumbnail="<id>"]     thumbnail that should play that video
 */
(function () {
  'use strict';

  var VISIBILITY_THRESHOLD = 0.2;

  function getGalleryElements(doc) {
    return {
      root: doc.querySelector('[data-gallery-video-root]'),
      videos: Array.prototype.slice.call(doc.querySelectorAll('[data-gallery-video]')),
      thumbnails: Array.prototype.slice.call(doc.querySelectorAll('[data-gallery-video-thumbnail]')),
    };
  }

  function findVideoById(videos, mediaId) {
    for (var i = 0; i < videos.length; i++) {
      if (videos[i].getAttribute('data-gallery-video') === mediaId) return videos[i];
    }
    return null;
  }

  function pauseVideo(video) {
    try {
      if (!video.paused) video.pause();
    } catch (error) {
      console.warn('[product-gallery-video] Unable to pause video', error);
    }
  }

  function playVideo(video) {
    try {
      var playResult = video.play();
      if (playResult && typeof playResult.catch === 'function') {
        playResult.catch(function (error) {
          console.warn('[product-gallery-video] Playback was blocked', error);
        });
      }
    } catch (error) {
      console.warn('[product-gallery-video] Unable to play video', error);
    }
  }

  function pauseOtherVideos(videos, activeVideo) {
    videos.forEach(function (video) {
      if (video !== activeVideo) pauseVideo(video);
    });
  }

  function handleThumbnailClick(thumbnail, videos) {
    var mediaId = thumbnail.getAttribute('data-gallery-video-thumbnail');
    var video = findVideoById(videos, mediaId);
    if (!video) {
      console.warn('[product-gallery-video] No video found for media id', mediaId);
      return;
    }
    pauseOtherVideos(videos, video);
    playVideo(video);
  }

  function handleSlideChange(swiper, videos) {
    var activeSlide = swiper.slides && swiper.slides[swiper.activeIndex];
    videos.forEach(function (video) {
      var isInActiveSlide = !!activeSlide && activeSlide.contains(video);
      if (!isInActiveSlide) pauseVideo(video);
    });
  }

  // Pause only — never auto-resume, so a shopper's pause is respected.
  function createVisibilityObserver(videos) {
    if (typeof IntersectionObserver === 'undefined') return null;
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) pauseVideo(entry.target);
      });
    }, { threshold: VISIBILITY_THRESHOLD });
    videos.forEach(function (video) {
      observer.observe(video);
    });
    return observer;
  }

  function initGalleryVideos(doc) {
    var elements = getGalleryElements(doc);
    if (!elements.videos.length) return;

    elements.videos.forEach(function (video) {
      video.setAttribute('controls', '');
      video.setAttribute('playsinline', '');
    });

    elements.thumbnails.forEach(function (thumbnail) {
      thumbnail.addEventListener('click', function () {
        handleThumbnailClick(thumbnail, elements.videos);
      });
    });

    // Playing one video pauses any other (e.g. native controls on another slide).
    elements.videos.forEach(function (video) {
      video.addEventListener('play', function () {
        pauseOtherVideos(elements.videos, video);
      });
    });

    var swiper = elements.root && elements.root.swiper;
    if (swiper && typeof swiper.on === 'function') {
      swiper.on('slideChange', function () {
        handleSlideChange(swiper, elements.videos);
      });
    } else {
      console.warn('[product-gallery-video] Gallery Swiper not found; slide-change pausing disabled');
    }

    var observer = createVisibilityObserver(elements.videos);
    if (observer) {
      doc.defaultView.addEventListener('pagehide', function () {
        observer.disconnect();
      }, { once: true });
    }
  }

  function handleDomReady() {
    try {
      initGalleryVideos(document);
    } catch (error) {
      console.error('[product-gallery-video] Initialisation failed', error);
    }
  }

  // Loaded at the end of <body>; DOMContentLoaded fires after the gallery's
  // own Swiper init listener, so the Swiper instance exists by then.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', handleDomReady);
  } else {
    handleDomReady();
  }
})();
